"use client"

import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import AccessDenied from "@/components/custom-access-denied"
import { useCheckPermission } from "@/hook/use-check-permission"
import { flushAllRedisCache } from "@/services/redis-cache"
import { SaveOutlined } from "@ant-design/icons"
import { Button, Form, Input, message } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import { t } from "../../../../languages/i18n"
import { PayloadSetting } from "../../ui-builder/types/types"
import useSettingService from "../service/settingService"

const UpsertForm = ({ editMode = false }: { editMode?: boolean }) => {
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const [form] = Form.useForm()
  const { TextArea } = Input
  const settingServices = useSettingService()
  const router = useRouter()
  const { hasPermission: checkPermission } = useCheckPermission()
  const [isFormReady, setIsFormReady] = useState(!editMode)

  const requiredPermission = editMode ? "update" : "create"

  const fetchSettingData = async (settingId: number) => {
    try {
      const settingDataFetch = await settingServices.getSettingById(settingId)
      if (settingDataFetch?.data?.attributes) {
        form.setFieldsValue({
          key: settingDataFetch.data.attributes.key,
          value: settingDataFetch.data.attributes.value,
          description: settingDataFetch.data.attributes.description,
        })
        setIsFormReady(true)
      } else {
        message.error(t("Failed to fetch setting data."))
      }
    } catch {
      message.error(t("An error occurred while fetching setting data."))
    }
  }

  const handleKeyBlur = async () => {
    const key = form.getFieldValue("key")

    if (key) {
      try {
        const keyExists = await settingServices.checkKeyExists(key)

        if (keyExists?.data?.length > 0) {
          message.error(t("Key already exists."))
          form.setFields([
            {
              name: "key",
              errors: [t("Key already exists")],
            },
          ])
          return false
        } else {
          form.setFields([{ name: "key", errors: [] }])
          return true
        }
      } catch {
        message.error(t("An error occurred while checking key existence."))
        return false
      }
    }
    return true
  }

  const handleFormSubmit = async (values: PayloadSetting) => {
    try {
      const payload: PayloadSetting = {
        key: values.key,
        value: values.value,
        description: values.description || "",
      }

      const isKeyValid = editMode || (await handleKeyBlur())
      if (!isKeyValid) {
        return
      }

      if (editMode) {
        await settingServices.updateSetting(Number(id), payload)
        try {
          await flushAllRedisCache()
        } catch {}
        router.push(`/admin/settings`)
        message.success(t("The setting has been edit successfully."))
        form.resetFields()
      } else {
        const res = await settingServices.createSettings(payload, form)
        if (res?.data) {
          try {
            await flushAllRedisCache()
          } catch {}
        }
        router.push(`/admin/settings`)
        if (res?.data) {
          message.success(t("The setting has been created successfully."))
          form.resetFields()
        }
      }
    } catch {
      message.error(t("An error occurred while creating/updating setting."))
    }
  }

  useEffect(() => {
    if (editMode && id) {
      fetchSettingData(Number(id))
    } else if (!editMode) {
      form.resetFields()
    }
  }, [id, editMode])

  if (!checkPermission("setting", requiredPermission)) {
    return <AccessDenied />
  }

  if (editMode && !isFormReady) {
    return <FormSkeleton mainSections={1} sidebarCards={0} />
  }

  const crumbs = [
    { title: t("Cài đặt"), href: `/admin/settings` },
    { title: editMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Cài đặt")} collapsible={false}>
      <Form.Item
        label={t("Key")}
        name="key"
        rules={[{ required: true, message: t("Khoá không được để trống") }]}
      >
        <Input onBlur={handleKeyBlur} disabled={editMode} />
      </Form.Item>

      <Form.Item
        label={t("Value")}
        name="value"
        rules={[{ required: true, message: t("Value cannot be left blank") }]}
      >
        <Input />
      </Form.Item>

      <Form.Item label={t("Description")} name="description" className="!mb-0">
        <TextArea rows={4} />
      </Form.Item>
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
          {editMode ? t("Edit") : t("Create")}
        </Button>
      }
    />
  )

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={editMode ? { id: id } : {}}
    >
      <FormShell
        title={editMode ? t("Chỉnh sửa cài đặt") : t("Thêm cài đặt mới")}
        crumbs={crumbs}
        main={main}
        actionBar={actionBar}
      />
    </Form>
  )
}

export default UpsertForm
